import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingBag, TrendingUp, Flame, Shield, Bell, Eye, Clock } from "lucide-react";
import { getHistoryEntries, getNotifications, getWatchlistItems } from "@/lib/watchlistStore";
import { getPendingPurchases } from "@/lib/intentCapture";
import { loadMemory } from "@/lib/userMemory";
import { getSavingsInsight, getMissedDeals, getStreak, recordActivity } from "@/lib/retentionEngine";
import { analyzeBehavior } from "@/lib/behaviorEngine";
import { useAuth } from "@/contexts/AuthContext";
import { getDecisionHistory, getRecentUnseen, getAgingDecisions } from "@/lib/cloudServices";
import PurchaseFollowUp from "./PurchaseFollowUp";
import { useNavigate } from "react-router-dom";

interface Props {
  onSearch: (query: string) => void;
  onCheckLink: (url: string) => void;
}

interface LastDecision {
  query: string;
  category?: string;
}

interface CloudAlert {
  id: string;
  message: string | null;
  alert_type: string;
  watchlist_items?: { product_name: string; query: string } | null;
}

interface AgingDecision {
  id: string;
  raw_query: string;
  winner_product_name: string | null;
  created_at: string;
}

export default function SessionContinuity({ onSearch, onCheckLink }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const history = getHistoryEntries();
  const notifications = getNotifications();
  const watchlist = getWatchlistItems();
  const pending = getPendingPurchases();
  const mem = loadMemory();
  const changedItems = watchlist.filter((i) => i.priceChange && i.priceChange !== 0);
  const betterRoutes = watchlist.filter((i) => i.signal === "Good time to buy");

  const streak = recordActivity();
  const savings = getSavingsInsight();
  const missedDeals = getMissedDeals();
  const behavior = analyzeBehavior();

  const [cloudLast, setCloudLast] = useState<LastDecision | null>(null);
  const [cloudAlerts, setCloudAlerts] = useState<CloudAlert[]>([]);
  const [agingDecisions, setAgingDecisions] = useState<AgingDecision[]>([]);

  useEffect(() => {
    if (user) {
      getDecisionHistory(user.id).then(({ data }) => {
        if (data.length > 0) {
          setCloudLast({
            query: data[0].raw_query,
            category: data[0].category ?? undefined,
          });
        }
      });
      getRecentUnseen(user.id).then((alerts) => {
        setCloudAlerts(alerts as CloudAlert[]);
      });
      getAgingDecisions(user.id).then((decisions) => {
        setAgingDecisions(decisions as AgingDecision[]);
      });
    }
  }, [user]);

  const lastQuery = user && cloudLast ? cloudLast : history[0] ? { query: history[0].query } : null;

  const hasContent = lastQuery || notifications.length > 0 || changedItems.length > 0 || pending.length > 0 || cloudAlerts.length > 0 || agingDecisions.length > 0 || watchlist.length >= 2;

  if (!hasContent) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-2xl mx-auto mb-8 space-y-3"
    >
      <PurchaseFollowUp />

      {/* Cloud alerts — price changed on tracked items */}
      {cloudAlerts.length > 0 && (
        <div className="rounded-xl border border-score-high/20 bg-score-high/5 px-4 py-3">
          <div className="flex items-center gap-2 mb-1.5">
            <Bell className="w-3.5 h-3.5 text-score-high" />
            <span className="text-xs text-score-high font-body font-medium">Price alerts</span>
          </div>
          {cloudAlerts.slice(0, 2).map((alert) => (
            <button
              key={alert.id}
              onClick={() => navigate("/watchlist")}
              className="w-full text-left py-1"
            >
              <p className="text-[11px] text-foreground font-body">{alert.message}</p>
            </button>
          ))}
        </div>
      )}

      {/* Check saved items CTA */}
      {watchlist.length >= 2 && cloudAlerts.length === 0 && (
        <button
          onClick={() => navigate("/watchlist")}
          className="w-full flex items-center justify-between rounded-xl border border-border/30 bg-card/30 px-3 py-2 hover:border-primary/20 transition-colors group"
        >
          <div className="flex items-center gap-2">
            <Eye className="w-3.5 h-3.5 text-muted-foreground/60" />
            <span className="text-xs text-muted-foreground font-body">Check {watchlist.length} saved items</span>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/60 group-hover:text-primary transition-colors shrink-0" />
        </button>
      )}

      {savings.vsAverageBuyer && (
        <div className="rounded-xl border border-accent/15 bg-accent/5 px-4 py-3">
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-accent" />
            <span className="text-xs text-accent font-body font-medium">{savings.vsAverageBuyer}</span>
          </div>
        </div>
      )}

      {streak.currentStreak >= 2 && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card/40">
          <Flame className="w-3.5 h-3.5 text-score-mid" />
          <span className="text-xs text-muted-foreground font-body">
            {streak.currentStreak}-day streak
          </span>
        </div>
      )}

      {missedDeals.length > 0 && (
        <div className="rounded-xl border border-score-mid/15 bg-score-mid/5 px-4 py-3">
          <p className="text-xs text-score-mid font-body font-medium mb-1.5">Changed since your last visit</p>
          {missedDeals.slice(0, 2).map((deal, i) => (
            <p key={i} className="text-[11px] text-muted-foreground font-body py-0.5">
              {deal.productName}: {deal.message}
            </p>
          ))}
        </div>
      )}

      {betterRoutes.length > 0 && (
        <div className="rounded-xl border border-score-high/20 bg-score-high/5 px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingBag className="w-3.5 h-3.5 text-score-high" />
            <span className="text-xs text-score-high font-body font-medium">Better route found</span>
          </div>
          <p className="text-xs text-muted-foreground font-body">{betterRoutes[0].productName}</p>
        </div>
      )}

      {changedItems.length > 0 && (
        <div className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3">
          <p className="text-xs text-primary font-body font-medium mb-2">
            Tracked items worth checking
          </p>
          {changedItems.slice(0, 2).map((item) => (
            <div key={item.id} className="flex items-center justify-between text-xs font-body py-1">
              <span className="text-foreground truncate mr-2">{item.productName}</span>
              <span className={item.priceChange! < 0 ? "text-score-high font-medium" : "text-score-low"}>
                {item.priceChange! > 0 ? "+" : ""}{item.priceChange}%
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Aging decisions — worth rechecking */}
      {agingDecisions.length > 0 && (
        <div className="rounded-xl border border-border/30 bg-card/30 px-4 py-3">
          <div className="flex items-center gap-2 mb-1.5">
            <Clock className="w-3.5 h-3.5 text-muted-foreground/60" />
            <span className="text-xs text-muted-foreground font-body font-medium">Worth rechecking</span>
          </div>
          {agingDecisions.slice(0, 1).map((d) => (
            <button
              key={d.id}
              onClick={() => onSearch(d.raw_query)}
              className="w-full text-left group"
            >
              <p className="text-[11px] text-foreground font-body group-hover:text-primary transition-colors">
                "{d.raw_query}" — decided {Math.floor((Date.now() - new Date(d.created_at).getTime()) / (24*60*60*1000))}d ago
              </p>
            </button>
          ))}
        </div>
      )}

      {lastQuery && (
        <button
          onClick={() => onSearch(lastQuery.query)}
          className="w-full flex items-center justify-between rounded-xl border border-border/30 bg-card/30 px-3 py-2 hover:border-primary/20 transition-colors group"
        >
          <div className="text-left">
            <p className="text-[10px] text-muted-foreground/70 font-body">Continue your last decision</p>
            <p className="text-xs text-foreground font-body truncate">{lastQuery.query}</p>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/60 group-hover:text-primary transition-colors shrink-0" />
        </button>
      )}
    </motion.div>
  );
}
